import styled from '@emotion/styled'
import HTMLReactParser from 'html-react-parser'
import { SubmitButtonLab } from 'platforms/kyoso-lab/shared'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'
import {
  EscButton,
  FormCheckBox,
  FormInput,
  FormRadioButtons,
  ModalBackground,
  PageInfo,
  SchoolGradeSelect,
} from 'shared'
import { COLORS, QUERIES } from 'styling/variables'
import {
  firstAndLastNameValidator,
  locationValidator,
  telephoneValidation,
  schoolNameValidator,
} from 'utils/helpers/validators'
import { formatDateAndTime } from 'utils/helpers/common'

function OrderModal({ modalData, saveModalCallback, closeModalCallback }) {
  const routeLocation = useLocation()
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    control,
    getValues,
    trigger,
    setValue,
    formState,
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      modal_paid: modalData?.paid,
      full_name: modalData.participant_info?.full_name || null,
      grade_id: modalData.participant_info?.grade_id || null,
      city: modalData.participant_info?.city || null,
      school: modalData.participant_info?.school || null,
      parent_names: modalData.participant_info?.parent_names || null,
      parent_phone: modalData.participant_info?.parent_phone || null,
      payment_type: modalData?.payment_method || null,
    },
  })

  const paymentTypeRadioButtons = [
    {
      id: 1,
      name: 'payment_type',
      label: t('hub:Participation:BankPayment'),
      register: register({ required: 'Required' }),
      error: formState.errors.payment_type,
    },
    {
      id: 2,
      name: 'payment_type',
      label: t('hub:Participation:ЕPay'),
      register: register({ required: 'Required' }),
      error: formState.errors.payment_type,
    },
  ]

  const closeModalHandler = (e) => {
    const backDrop = document.getElementById('modal-background')

    if (e.target === backDrop) {
      closeModalCallback()
    }
  }

  const onSubmitHandler = (data) => {
    const { grade_id } = modalData.participant_info

    const touchedFields = Object.keys(formState.touched).filter(
      (field) => field !== 'paymentType',
    )
    const newUserData = { ...data }
    delete newUserData.payment_type
    delete newUserData.modal_paid
    let isDataTouched = false

    if (touchedFields.length) {
      isDataTouched = true
    } else if (data.grade_id != grade_id) {
      isDataTouched = true
    }

    saveModalCallback(
      modalData.id,
      isDataTouched,
      newUserData,
      data.payment_type,
      data.modal_paid,
    )
  }

  return (
    <ModalBackground id="modal-background" onClick={closeModalHandler}>
      <ModalWrap>
        <ModalLayoutForm onSubmit={handleSubmit(onSubmitHandler)}>
          <ActionsContainer>
            <SubmitButtonLab
              className="black"
              customCss={{ maxWidth: 200 }}
              type="submit"
            >
              {t('lab:Shared:SubmitButtonSave')}
            </SubmitButtonLab>
            <EscButton
              onClick={() => closeModalCallback()}
              customCss={{ position: 'relative', top: 'auto', right: 'auto' }}
            />
          </ActionsContainer>

          <HeaderContainer>
            <div id="order-info">
              <PageInfo
                title={t('lab:KyosoOrders:OrdersModalTitle', {
                  id: routeLocation.state?.id,
                  name: routeLocation.state?.name,
                  grade_name: routeLocation.state?.grade_name,
                })}
              />
              {modalData.participant_id && (
                <p id="participant-id">
                  <span>{t('lab:KyosoOrders:ParticipantId')}: </span>
                  <span className="blue">{modalData.participant_id}</span>
                </p>
              )}

              <p id="order-id">
                <span>{t('lab:KyosoOrders:OrderId')}: </span>
                <span className="blue">{modalData.order_id}</span>
              </p>

              {modalData.from_bundle_order && (
                <p id="bundle_name">
                  <span>{t('lab:KyosoOrders:BundleParticipation')}: </span>
                  <span className="blue">{modalData.bundle_name}</span>
                </p>
              )}

              <p id="order-date">
                <span>{t('lab:KyosoOrders:OrderDate')}: </span>
                <span className="blue">
                  {formatDateAndTime(modalData.order_date)}
                </span>
              </p>

              <p id="participant-email">
                <span>{t('lab:KyosoOrders:ParticipantEmail')}: </span>
                <span className="blue">{modalData.participant_info.email}</span>
              </p>

              <p id="warning-text" className="red">
                {t('lab:KyosoOrders:ModalParticipentWarning')}
              </p>
            </div>

            {+modalData?.payment_method && !modalData.from_bundle_order ? (
              <div id="order-status">
                <h3 id="status-title">{t('lab:KyosoOrders:StatusTitle')}</h3>

                <p id="status-warning" className="red">
                  {t('lab:KyosoOrders:StatusWarning')}
                </p>

                <FormCheckBox
                  key="modal-paid"
                  id="paid"
                  name="modal_paid"
                  label={t('lab:KyosoOrders:FilterCheckboxPayed')}
                  register={register()}
                  customCss={{
                    marginBottom: 10,
                    marginTop: 10,

                    ' .text': {
                      marginLeft: '10px',
                      gridColumn: '2',
                      justifySelf: 'left',
                    },
                  }}
                />

                <p id="status-message" className="green">
                  {HTMLReactParser(t('lab:KyosoOrders:StatusMessage'))}
                </p>
              </div>
            ) : null}
          </HeaderContainer>

          <PageInfo
            title={t('hub:Participation:UserDataTitle')}
            customCss={{
              '& h1': {
                fontSize: 22,
                marginBottom: 20,
                marginTop: 0,
              },
            }}
          />
          <ParticipantInfo>
            <FormInput
              customCss={{
                marginBottom: '0',
                gridColumn: '1 / 3',
                gridRow: '1',
              }}
              id="fullName"
              type="text"
              name="full_name"
              label={`${t('shared:LabelFirstAndLastName')} *`}
              register={register({
                required: 'Required',
                validate: {
                  firstAndLastNameValidator: (value) =>
                    firstAndLastNameValidator(value),
                },
              })}
              error={formState.errors.full_name}
              touched={formState.touched.full_name}
            />

            <SchoolGradeSelect
              id="schoolGrade"
              customCss={{ marginBottom: '0' }}
              label={`${t('shared:LabelSchoolClass')} *`}
              name="grade_id"
              setValue={setValue}
              getValues={getValues}
              description="SchoolClassDescription"
              control={control}
              rules={{ required: 'Required' }}
              isMulti={false}
              defaultValue={null}
              translationKeys={{
                lastYear: `${new Date().getFullYear() - 1}`,
                currentYear: `${new Date().getFullYear()}`,
              }}
              error={formState.errors.grade_id}
            />

            <FormInput
              customCss={{
                marginBottom: '0',
                gridColumn: '1/ 2',
                gridRow: '2',
              }}
              id="town"
              type="text"
              name="city"
              label="LabelTown"
              description={'TownDescription'}
              register={register({
                validate: {
                  locationValidator: (value) => locationValidator(value),
                },
              })}
              error={formState.errors.city}
              touched={formState.touched.city}
            />

            <FormInput
              customCss={{
                marginBottom: '0',
                gridColumn: '2/ 4',
                gridRow: '2',
              }}
              id="schoolName"
              type="text"
              name="school"
              label="LabelSchoolName"
              description={'SchoolNameDescription'}
              onBlur={(e) => trigger('school')}
              register={register({
                validate: {
                  schoolNameValidator: (value) => schoolNameValidator(value),
                },
              })}
              error={formState.errors.school}
              touched={formState.touched.school}
            />

            <div
              style={{
                width: '100%',
                gridColumn: '1/ 4',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 20,
              }}
            >
              <FormInput
                customCss={{
                  marginBottom: '0',
                }}
                id="parentName"
                type="text"
                description={'TownDescription'}
                name="parent_names"
                label="LabelFirstAndLastNameParent"
                register={register({
                  validate: {
                    firstAndLastNameValidator: (value) =>
                      firstAndLastNameValidator(value),
                  },
                })}
                error={formState.errors.parent_names}
                touched={formState.touched.parent_names}
              />

              <FormInput
                customCss={{
                  marginBottom: '0',
                }}
                id="parentPhone"
                type="tel"
                description={'TownDescription'}
                name="parent_phone"
                label="LabelTelephone"
                register={register({
                  validate: {
                    telephoneValidation: (value) => {
                      if (value) return telephoneValidation(value)
                    },
                  },
                })}
                error={formState.errors.parent_phone}
                touched={formState.touched.parent_phone}
              />
            </div>
          </ParticipantInfo>

          {+modalData?.payment_method && !modalData.from_bundle_order ? (
            <>
              <FormRadioButtons
                customCss={{
                  gridRow: '2',
                  marginBottom: 10,
                  '#label-wrapper': {
                    marginBottom: 10,
                    '#label': {
                      color: COLORS.secBlack,
                    },
                  },
                }}
                disabled={modalData.payment_method === '1'}
                radioButtons={paymentTypeRadioButtons}
                label={'hub:Participation:PaymentTypeLabel'}
                id="paymentType"
                type="radio"
                name="payment_type"
                register={register()}
                error={formState.errors.payment_type}
              />
              <p id="modal-message">{t('lab:KyosoOrders:ModalMessage')}</p>
            </>
          ) : null}

          <SubmitButtonLab
            className="black"
            customCss={{ maxWidth: 200, alignSelf: 'center', marginTop: 20 }}
            type="submit"
          >
            {t('lab:Shared:SubmitButtonSave')}
          </SubmitButtonLab>
        </ModalLayoutForm>
      </ModalWrap>
    </ModalBackground>
  )
}

export { OrderModal }

const ModalWrap = styled('div')({
  display: 'table-cell',
  verticalAlign: 'middle',
})

const ModalLayoutForm = styled('form')({
  position: 'relative',
  marginLeft: 'auto',
  marginRight: 'auto',
  width: '95%',
  maxHeight: '90%',
  maxWidth: 1400,
  background: '#FFFFFF 0% 0% no-repeat padding-box',
  boxShadow: '0px 0px 5px #0000000D',
  borderRadius: 25,
  padding: '90px 40px 40px 40px',
  fontFamily: 'Balsamiq Sans',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',

  '.blue': {
    color: COLORS.secBlue,
  },

  '.red': {
    color: COLORS.secRed,
  },

  '.green': {
    color: COLORS.secGreen,
  },

  '::-webkit-scrollbar': {
    display: 'none',
  },

  [`@media (max-width: ${QUERIES.mobile}px)`]: {
    padding: '30px 20px',
  },

  '.answer-type-label': {
    color: COLORS.secBlack,
    fontSize: 18,
    marginBottom: 20,

    '.answer-type-error': {
      color: COLORS.secRed,
    },
  },
})

const ActionsContainer = styled('div')({
  position: 'absolute',
  top: 20,
  right: 30,
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 30,
})

const HeaderContainer = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 30,
  marginBottom: 30,

  '#order-info': {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },

  '#order-status': {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    border: `2px solid ${COLORS.borderGray}`,
    borderRadius: 10,
    padding: 15,
    gap: 10,

    '#status-title': {
      textAlign: 'center',
    },
  },
})

const ParticipantInfo = styled('div')({
  display: 'grid',
  alignContent: 'center',
  gridTemplateColumns: '1fr 1fr 1fr',
  gridGap: '30px',
  alignItems: 'end',
  marginBottom: 30,
  width: '70%',
})
